<script lang="ts">
	import '@fontsource-variable/manrope/wght.css';
	import '../app.css';
	import { onMount } from 'svelte';
	import { afterNavigate } from '$app/navigation';
	import { authActions, authStore } from '$lib/stores/auth';
	import { themeUtils } from '$lib/stores/theme';
	import { getQueryClient } from '$lib/query/client';
	import { ModeWatcher } from 'mode-watcher';
	import { Toaster } from '$lib/ui/primitives/sonner/index.js';
	import type { LayoutData } from './$types';
	import { identifyUser, trackPageView } from '$lib/services/posthog';
	import { ConsentBanner, consentStore } from '$lib/features/consent';
	import { enableMocking } from '$lib/mocks/index';
	import { ReloadPrompt } from '$lib/features/pwa';
	import { TimezoneSuggestPrompt } from '$lib/features/settings/components/TimezoneSuggestPrompt';
	import { setupTitlePrefix } from '$lib/utils/title-prefix';
	import { SessionBoundary } from '$lib/features/auth/components/SessionBoundary';
	import { registerTenantSessionReset } from '$lib/features/auth/logic/tenantSessionReset';
	import { toast } from 'svelte-sonner';

	let { data, children }: { data: LayoutData; children: any } = $props();
	// Get the QueryClient instance
	const queryClient = getQueryClient();

	// Store cleanup function references for onMount return
	let themeCleanup: (() => void) | undefined;
	let unsubscribeAuth: (() => void) | undefined;
	let titleCleanup: (() => void) | undefined;
	let authUserId = $state<string | null>(null);
	let identifiedUserId: string | null = null;

	// Identification is UUID-only and becomes eligible only after explicit
	// analytics consent. Reset the local guard when consent or authentication is
	// removed so the same user is identified again after a later opt-in/login.
	$effect(() => {
		if (consentStore.decision === 'accepted' && authUserId) {
			if (identifiedUserId !== authUserId) {
				if (identifyUser(authUserId)) identifiedUserId = authUserId;
			}
		} else {
			identifiedUserId = null;
		}
	});

	afterNavigate(({ to }) => {
		if (to) trackPageView(to.url.pathname);
	});

	// Initialize theme and auth state on app startup
	onMount(() => {
		const unregisterToastReset = registerTenantSessionReset(() => toast.dismiss());
		unsubscribeAuth = authStore.subscribe((state) => {
			authUserId = state.isAuthenticated ? (state.user?.id ?? null) : null;
		});

		// Use IIFE to properly await async initialization
		(async () => {
			// Enable MSW mocking in test mode (must be first, awaited to prevent race conditions)
			await enableMocking();

			// Prefix document title with [dev]/[msw] in non-prod modes
			titleCleanup = setupTitlePrefix();

			// Reading consent is the only startup path that may initialize PostHog.
			// Without stored acceptance (or with DNT), the SDK is never initialized.
			consentStore.init();

			// Initialize theme system first (for visual consistency)
			themeCleanup = themeUtils.initialize();

			// Initialize auth state from server data
			// This prevents flash of unauthenticated content
			if (data.isAuthenticated) {
				// We know user is authenticated from server
				// Initialize full auth state (fetch user data)
				authActions.initAuth();
			}
		})();

		// Return cleanup function (called synchronously by Svelte)
		return () => {
			unregisterToastReset();
			themeCleanup?.();
			unsubscribeAuth?.();
			titleCleanup?.();
		};
	});
</script>

<!-- samlla#kyqh: on mobile (<600px) toasts span the full width at the top; start them
     below the AppShell header (~69px) so a persistent toast never covers the menu button. -->
<Toaster richColors position="top-right" mobileOffset={{ top: 80 }} />
<ReloadPrompt />
<TimezoneSuggestPrompt />
<ConsentBanner />
<ModeWatcher themeColors={{ dark: '#171717', light: '#ffffff' }} />
<SessionBoundary client={queryClient}>
	{@render children()}
</SessionBoundary>
